/**
 * DXF byte-stream → string decoding honouring `$DWGCODEPAGE` (T9 IMPORT —
 * Cyrillic / non-ASCII mojibake-on-open fix; symmetric counterpart to the
 * T9 PR-3 `\U+` EXPORT escape in {@link ./dxfUnicodeEscape}).
 *
 * ## The bug
 *
 * Every production DXF import historically read the file bytes via
 * `File.text()` / `Blob.text()`. Per the WHATWG spec those APIs decode
 * **UTF-8 ONLY** — they ignore the file's declared `$DWGCODEPAGE`. A real
 * AutoCAD pre-R2007 file authored as `$DWGCODEPAGE = ANSI_1251` carries its
 * text as windows-1251 BYTES; read as UTF-8 every Cyrillic byte becomes a
 * U+FFFD replacement char → "кракозябры" the moment the file is opened.
 *
 * ## The fix (ezdxf-faithful)
 *
 * ezdxf reads a DXF file by first sniffing the codepage from the byte head
 * (`$DWGCODEPAGE`, falling back to cp1252) and then re-decoding the whole
 * stream in that codec — EXCEPT for R2007+ (`$ACADVER >= AC1021`) where the
 * format mandates UTF-8 regardless of `$DWGCODEPAGE`.
 * Reference: https://ezdxf.readthedocs.io/en/stable/dxfinternals/fileencoding.html
 *
 * We do the same here at the IO boundary. `$DWGCODEPAGE` / `$ACADVER` and the
 * DXF group-code structure are pure ASCII, so they are byte-identical across
 * every supported codepage — we can latin1-decode the head (1 byte → 1 char,
 * never throws) to sniff them safely before committing to the real codec.
 *
 * Every label we emit (`windows-125x`, `windows-874`, `shift_jis`, `gbk`,
 * `euc-kr`, `big5`, `utf-8`) is a WHATWG-standard `TextDecoder` label
 * supported natively in Node and every browser — ZERO new dependencies.
 *
 * This runs at the BYTE boundary, BEFORE tokenization. PR-3's
 * {@link ./dxfUnicodeEscape#decodeDxfUnicode} runs DURING parse on already-
 * decoded STRINGS (it resolves `\U+XXXX` inline escapes). The two are
 * orthogonal and compose without double-decoding:
 *   - raw cp1251 bytes (no `\U+`)      → byte-decode fixes it; `\U+` is a no-op
 *   - ASCII bytes with `\U+` escapes   → byte-decode is a no-op; `\U+` resolves
 *   - both                             → byte-decode yields Cyrillic, then `\U+`
 *                                        resolves the escapes — single pass each
 */

/**
 * Map a `$DWGCODEPAGE` HEADER value (the DXF group-3 string under the
 * `$DWGCODEPAGE` variable, upper-cased) to its WHATWG `TextDecoder` label.
 *
 * The `ANSI_125x` family are the Windows code pages AutoCAD uses for Western,
 * Central-European, Cyrillic, Greek, Turkish, Baltic, Arabic, Hebrew and
 * Vietnamese text; `ANSI_874` is Thai; the four CJK pages (932/936/949/950)
 * cover Japanese / Simplified-Chinese / Korean / Traditional-Chinese. Every
 * value on the right is a label `new TextDecoder(label)` accepts natively.
 *
 * Source: Autodesk DXF `$DWGCODEPAGE` documentation + the WHATWG Encoding
 * standard label table. Keys are stored upper-cased; the caller upper-cases
 * the parsed name before the lookup.
 */
export const DXF_CODEPAGE_TO_LABEL: Record<string, string> = {
  ANSI_1250: "windows-1250", // Central European
  ANSI_1251: "windows-1251", // Cyrillic
  ANSI_1252: "windows-1252", // Western European (Latin-1 superset)
  ANSI_1253: "windows-1253", // Greek
  ANSI_1254: "windows-1254", // Turkish
  ANSI_1255: "windows-1255", // Hebrew
  ANSI_1256: "windows-1256", // Arabic
  ANSI_1257: "windows-1257", // Baltic
  ANSI_1258: "windows-1258", // Vietnamese
  ANSI_874: "windows-874", // Thai
  ANSI_932: "shift_jis", // Japanese
  ANSI_936: "gbk", // Simplified Chinese
  ANSI_949: "euc-kr", // Korean
  ANSI_950: "big5", // Traditional Chinese
  "UTF-8": "utf-8",
  UTF8: "utf-8",
};

/**
 * The codepage we fall back to when no `$DWGCODEPAGE` is present and the file
 * is pre-R2007 — matches ezdxf's `cp1252` default (a Latin-1 superset, so it
 * never mangles ASCII and is the safest Western guess).
 */
const DEFAULT_LABEL = "windows-1252";

/**
 * How many leading bytes we latin1-scan to sniff the codepage. The HEADER
 * section (where `$ACADVER` and `$DWGCODEPAGE` live) is at the very top of a
 * DXF file — a few hundred bytes in for a real file. 8 KB is a generous
 * upper bound that comfortably covers the HEADER even with a large `$FINGERPRINTGUID`
 * / `$VERSIONGUID` preamble, while bounding the sniff cost on a huge file to a
 * fixed-size head read (we never latin1-decode the whole multi-MB stream just
 * to find the codepage).
 */
const SNIFF_HEAD_BYTES = 8192;

/**
 * The R2007 DXF version marker (`$ACADVER = AC1021`). At and above this
 * version the DXF format mandates UTF-8 text regardless of any `$DWGCODEPAGE`
 * value — AutoCAD writes `$DWGCODEPAGE = ANSI_1252` into R2007+ files for
 * legacy-reader compatibility but the bytes are UTF-8. We must NOT re-decode
 * such a file in the declared codepage (that would double-decode the UTF-8
 * Cyrillic into garbage), so an `$ACADVER >= AC1021` short-circuits to utf-8.
 *
 * The `AC10xx` tokens sort by their two trailing digits within the AC10
 * family: AC1009 (R12) < AC1012 (R13) < AC1014 (R14) < AC1015 (R2000) <
 * AC1018 (R2004) < AC1021 (R2007) < AC1024 (R2010) < AC1027 (R2013) <
 * AC1032 (R2018). Numeric compare of the trailing integer is exact across
 * the whole family.
 */
const ACADVER_R2007 = 1021;

/**
 * Match the `$ACADVER` HEADER variable and capture its `AC10xx` value.
 *
 * DXF group layout in the HEADER section:
 *
 *   9
 *   $ACADVER
 *   1
 *   AC1015
 *
 * i.e. group-9 name line `$ACADVER`, then a group-1 string value line. We
 * tolerate `\r\n` line endings and leading spaces on each line (some writers
 * indent). The capture group holds the four digits after `AC` (e.g. `1015`).
 */
const ACADVER_RE = /\s*9\r?\n\s*\$ACADVER\r?\n\s*1\r?\n\s*AC(\d{4})/;

/**
 * Match the `$DWGCODEPAGE` HEADER variable and capture its NAME value.
 *
 *   9
 *   $DWGCODEPAGE
 *   3
 *   ANSI_1251
 *
 * group-9 name `$DWGCODEPAGE`, then a group-3 string value. Same `\r\n` +
 * leading-space tolerance as {@link ACADVER_RE}. The capture group holds the
 * raw codepage name (e.g. `ANSI_1251`), which the caller upper-cases before
 * looking it up in {@link DXF_CODEPAGE_TO_LABEL}.
 */
const DWGCODEPAGE_RE = /\s*9\r?\n\s*\$DWGCODEPAGE\r?\n\s*3\r?\n\s*([^\r\n]+)/;

/** UTF-8 byte-order-mark bytes (`EF BB BF`). */
const UTF8_BOM_0 = 0xef;
const UTF8_BOM_1 = 0xbb;
const UTF8_BOM_2 = 0xbf;

/** Leading U+FEFF (a decoded UTF-8 BOM) — stripped from the decoded string. */
const BOM_CHAR = "﻿";

/**
 * Sniff the `TextDecoder` label for a DXF byte buffer WITHOUT decoding the
 * whole stream. Resolution order (ezdxf-faithful):
 *
 *   1. UTF-8 BOM (`EF BB BF`) at the very start → `utf-8` (an explicit BOM
 *      overrides everything; the bytes are UTF-8 by definition).
 *   2. latin1-decode ONLY the first {@link SNIFF_HEAD_BYTES} bytes — a 1-byte→
 *      1-char mapping that never throws and is byte-exact for the pure-ASCII
 *      `$ACADVER` / `$DWGCODEPAGE` / group lines regardless of the true codec.
 *   3. `$ACADVER >= AC1021` (R2007+) → `utf-8` (format-mandated; ignore
 *      `$DWGCODEPAGE`, which AutoCAD writes as `ANSI_1252` for legacy readers
 *      even though the bytes are UTF-8).
 *   4. else `$DWGCODEPAGE` → {@link DXF_CODEPAGE_TO_LABEL}[NAME] when the name
 *      is a label we know.
 *   5. default {@link DEFAULT_LABEL} (`windows-1252`, ezdxf's default).
 *
 * Only the head is scanned, so a multi-MB file costs a fixed ≤8 KB latin1
 * decode here; the real (potentially large) decode happens once in
 * {@link decodeDxfBytes}.
 */
export function sniffDxfEncoding(bytes: Uint8Array): string {
  // (1) explicit UTF-8 BOM wins outright.
  if (
    bytes.length >= 3 &&
    bytes[0] === UTF8_BOM_0 &&
    bytes[1] === UTF8_BOM_1 &&
    bytes[2] === UTF8_BOM_2
  ) {
    return "utf-8";
  }

  // (2) latin1-decode the HEAD ONLY (never the whole stream). latin1 is a
  // total 1-byte→1-char map: it never throws and is byte-identical to the
  // real codec across the pure-ASCII header lines we regex below.
  const head = bytes.subarray(0, Math.min(bytes.length, SNIFF_HEAD_BYTES));
  const headText = new TextDecoder("latin1").decode(head);

  // (3) R2007+ ($ACADVER >= AC1021) is UTF-8 regardless of $DWGCODEPAGE.
  const acadVerMatch = ACADVER_RE.exec(headText);
  if (acadVerMatch) {
    const ver = Number.parseInt(acadVerMatch[1], 10);
    if (Number.isFinite(ver) && ver >= ACADVER_R2007) {
      return "utf-8";
    }
  }

  // (4) pre-R2007: honour $DWGCODEPAGE when it names a codec we map.
  const codepageMatch = DWGCODEPAGE_RE.exec(headText);
  if (codepageMatch) {
    const name = codepageMatch[1].trim().toUpperCase();
    const label = DXF_CODEPAGE_TO_LABEL[name];
    if (label) return label;
  }

  // (5) ezdxf default.
  return DEFAULT_LABEL;
}

/**
 * Decode a DXF file's raw bytes to a string using the codepage sniffed from
 * its head — the codepage-honouring replacement for `File.text()` /
 * `Blob.text()` (which are UTF-8 only and mojibake every non-UTF-8 file).
 *
 * Accepts either an `ArrayBuffer` (from `file.arrayBuffer()`) or a
 * `Uint8Array`. Sniffs once via {@link sniffDxfEncoding}, decodes the WHOLE
 * stream once with a non-fatal `TextDecoder` (a malformed byte becomes U+FFFD
 * rather than throwing — a partial read still surfaces text), and strips a
 * single leading U+FEFF BOM char if the decode produced one.
 */
export function decodeDxfBytes(bytes: ArrayBuffer | Uint8Array): string {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const label = sniffDxfEncoding(u8);
  let s = new TextDecoder(label, { fatal: false }).decode(u8);
  // A UTF-8 BOM decodes to a leading U+FEFF; strip it so the first token line
  // is clean (tokenizers expect the file to start with `0` / `999` / `  9`,
  // not an invisible BOM char). Only ever one, only at position 0.
  if (s.charCodeAt(0) === 0xfeff) {
    s = s.slice(BOM_CHAR.length);
  }
  return s;
}
